import { useEffect, useMemo } from 'react';

import { debounce, isEqual } from 'lodash';
import useSWR, { mutate } from 'swr';

import { apiClient } from '@src/clients';

const inProgress = new Set<string>();
const refetch = async (CACHE_KEY: string) => await mutate(CACHE_KEY);
const debouncedRefetch = debounce(refetch, 2000);
let staticInstance = 0;

interface UseGetVisualizationByIndexId {
  aoi_id: string | null;
  index_id: string | null;
}

export const useGetVisualizationByIndexId = ({
  aoi_id,
  index_id
}: UseGetVisualizationByIndexId) => {
  const CACHE_KEY = `${aoi_id}/visualizations/${index_id}`;

  const { data, isLoading } = useSWR(
    aoi_id && index_id ? CACHE_KEY : null,
    () =>
      apiClient.atlasApi.v2.listVisualizationsV2AoisAoiIdVisualizationsGet(
        aoi_id!,
        { index_id }
      ),
    {
      onErrorRetry: error => {
        if (error.status === 404) return;
      },
      onSuccess: data => {
        const visualizations = data.data.visualization;

        for (const visualization of visualizations) {
          if (visualization.progress !== 100) {
            inProgress.add(visualization.id);
            continue;
          }

          inProgress.delete(visualization.id);
        }

        if (inProgress.size > 0) debouncedRefetch(CACHE_KEY);
      },
      refreshInterval: 0,
      compare: (a, b) => isEqual(a?.data.visualization, b?.data.visualization)
    }
  );

  useEffect(() => {
    staticInstance++;

    return () => {
      staticInstance--;
      if (staticInstance === 0) debouncedRefetch.cancel();
    };
  }, []);

  return useMemo(
    () => ({
      visualizationByIndexId: (data?.data.visualization ?? [])[0],
      isVisualizationIndexIdLoading: isLoading,
      mutateVisualizationByIndexId: async () => {
        debouncedRefetch.cancel();
        await refetch(CACHE_KEY);
      }
    }),
    [data, isLoading]
  );
};
